import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { transportApi } from '@/services/transportApi';

const FUEL_TYPES = ['petrol', 'diesel', 'cng', 'ev'];
const DOC_FIELDS = [['insurance', 'Insurance'], ['puc', 'PUC / Pollution'], ['fitness', 'Fitness'], ['permit', 'Permit'], ['road_tax', 'Road Tax']];
const EMPTY = { registration_no: '', vehicle_type: '', make_model: '', fuel_type: 'diesel', capacity: '', ownership: 'owned', hire_type: 'vehicle_only', vendor_driver_name: '', vendor_driver_phone: '', vendor_driver_licence: '', default_driver_id: '', eligible_driver_ids: [], doc_dates: {}, home_base_location_id: '', active: true };

const Vehicles = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [v, d, s] = await Promise.all([transportApi.listVehicles(), transportApi.listDrivers({ active: true }), transportApi.getSettings()]);
    setVehicles(v); setDrivers(d); setTypes(s.vehicle_types || []);
    try { setLocations(await transportApi.listLocations({ active: true })); } catch { /* ignore */ }
  };
  useEffect(() => { load().catch(() => toast.error('Failed to load vehicles')); }, []);

  const openNew = () => { setForm({ ...EMPTY, vehicle_type: types[0] || '' }); setEditId(null); setOpen(true); };
  const openEdit = (v) => { setForm({ ...EMPTY, ...v, doc_dates: v.doc_dates || {} }); setEditId(v.id); setOpen(true); };

  const save = async () => {
    if (!form.registration_no.trim() || !form.vehicle_type) { toast.error('Registration no. and type are required'); return; }
    try {
      const payload = { ...form, default_driver_id: form.default_driver_id || null };
      if (editId) await transportApi.updateVehicle(editId, payload);
      else await transportApi.createVehicle(payload);
      toast.success('Vehicle saved'); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => { if (!window.confirm('Deactivate this vehicle?')) return; await transportApi.deleteVehicle(id); load(); };
  const toggleEligible = (id) => setForm(f => ({ ...f, eligible_driver_ids: f.eligible_driver_ids.includes(id) ? f.eligible_driver_ids.filter(x => x !== id) : [...f.eligible_driver_ids, id] }));

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="transport-vehicles-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/transport')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-xl font-bold font-display">Vehicle Master</h1>
          </div>
          <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-600" data-testid="vehicle-add-btn"><Plus className="w-4 h-4 mr-1" />Add Vehicle</Button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Card className="border-0 shadow-md"><CardContent className="p-0">
          <Table data-testid="vehicles-table">
            <TableHeader><TableRow>
              <TableHead>Registration</TableHead><TableHead>Type</TableHead><TableHead>Fuel</TableHead>
              <TableHead>Ownership</TableHead><TableHead>Eligible Drivers</TableHead><TableHead>Active</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {vehicles.map(v => (
                <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`}>
                  <TableCell className="font-semibold">{v.registration_no}</TableCell>
                  <TableCell>{v.vehicle_type}</TableCell>
                  <TableCell className="capitalize">{v.fuel_type}</TableCell>
                  <TableCell>
                    {v.ownership === 'hired'
                      ? <div className="flex flex-col gap-0.5"><Badge className="bg-orange-100 text-orange-700 w-fit">Hired</Badge><span className="text-[10px] text-gray-500">{v.hire_type === 'with_driver' ? 'Vehicle + Driver' : 'Vehicle only'}</span></div>
                      : <Badge variant="secondary">Owned</Badge>}
                  </TableCell>
                  <TableCell className="text-xs text-gray-600" data-testid={`vehicle-drivers-cell-${v.id}`}>{v.ownership === 'hired' && v.hire_type === 'with_driver' ? (v.vendor_driver_name ? `${v.vendor_driver_name}${v.vendor_driver_phone ? ' · ' + v.vendor_driver_phone : ''} (vendor)` : '— (vendor)') : ((v.eligible_driver_names || []).join(', ') || '—')}</TableCell>
                  <TableCell>{v.active ? <Badge className="bg-green-100 text-green-700">Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(v)} data-testid={`vehicle-edit-${v.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(v.id)} data-testid={`vehicle-delete-${v.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {vehicles.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8" data-testid="vehicles-empty">No vehicles yet. Add your first vehicle.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="vehicle-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Edit' : 'Add'} Vehicle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Registration No.</Label><Input value={form.registration_no} onChange={e => setForm(f => ({ ...f, registration_no: e.target.value }))} data-testid="vehicle-reg-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vehicle Type</Label>
                <Select value={form.vehicle_type} onValueChange={v => setForm(f => ({ ...f, vehicle_type: v }))}>
                  <SelectTrigger data-testid="vehicle-type-select"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>{types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Fuel Type</Label>
                <Select value={form.fuel_type} onValueChange={v => setForm(f => ({ ...f, fuel_type: v }))}>
                  <SelectTrigger data-testid="vehicle-fuel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{FUEL_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Make / Model</Label><Input value={form.make_model} onChange={e => setForm(f => ({ ...f, make_model: e.target.value }))} data-testid="vehicle-model-input" /></div>
              <div><Label>Capacity (load/seats)</Label><Input value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} data-testid="vehicle-capacity-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ownership</Label>
                <Select value={form.ownership} onValueChange={v => setForm(f => ({ ...f, ownership: v }))}>
                  <SelectTrigger data-testid="vehicle-ownership-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="owned">Owned</SelectItem><SelectItem value="hired">Hired / Rented</SelectItem></SelectContent>
                </Select>
              </div>
              {form.ownership === 'hired' && (
                <div><Label>Hire Type</Label>
                  <Select value={form.hire_type || 'vehicle_only'} onValueChange={v => setForm(f => ({ ...f, hire_type: v }))}>
                    <SelectTrigger data-testid="vehicle-hire-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vehicle_only">Vehicle only (company driver)</SelectItem>
                      <SelectItem value="with_driver">Vehicle + Driver (vendor's driver)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {form.ownership === 'hired' && form.hire_type === 'with_driver' ? (
              <div className="rounded-lg border border-orange-200 bg-orange-50/60 p-3 space-y-3" data-testid="vehicle-vendor-driver-block">
                <p className="text-xs font-medium text-orange-700">Vendor's driver comes with this vehicle — no company driver needs to be assigned.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Vendor Driver Name</Label><Input value={form.vendor_driver_name} onChange={e => setForm(f => ({ ...f, vendor_driver_name: e.target.value }))} data-testid="vehicle-vendor-driver-name" /></div>
                  <div><Label>Vendor Driver Phone</Label><Input value={form.vendor_driver_phone} onChange={e => setForm(f => ({ ...f, vendor_driver_phone: e.target.value }))} data-testid="vehicle-vendor-driver-phone" /></div>
                </div>
                <div><Label>Driver Licence No. (optional)</Label><Input value={form.vendor_driver_licence} onChange={e => setForm(f => ({ ...f, vendor_driver_licence: e.target.value }))} data-testid="vehicle-vendor-driver-licence" /></div>
              </div>
            ) : (
              <>
                <div><Label>Default Driver</Label>
                  <Select value={form.default_driver_id || 'none'} onValueChange={v => setForm(f => ({ ...f, default_driver_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="vehicle-default-driver-select"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Eligible Drivers (only these can be auto-assigned)</Label>
                  <div className="border rounded-lg p-2 max-h-32 overflow-y-auto space-y-1" data-testid="vehicle-eligible-list">
                    {drivers.length === 0 && <p className="text-xs text-gray-400">Add drivers first.</p>}
                    {drivers.map(d => (
                      <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`vehicle-eligible-${d.id}`}>
                        <input type="checkbox" checked={form.eligible_driver_ids.includes(d.id)} onChange={() => toggleEligible(d.id)} />
                        {d.name} <span className="text-xs text-gray-400">{d.phone}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div>
              <Label>Home Base (where it parks between trips)</Label>
              <Select value={form.home_base_location_id || 'none'} onValueChange={v => setForm(f => ({ ...f, home_base_location_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="vehicle-home-base-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-gray-400 mt-1">Used as the vehicle's location until it has run a trip. After each trip it moves to the last drop. Add places in Saved Locations.</p>
            </div>
            <div>
              <Label>Document Expiry Dates</Label>
              <div className="grid grid-cols-2 gap-2">
                {DOC_FIELDS.map(([k, lbl]) => (
                  <div key={k}><span className="text-xs text-gray-500">{lbl}</span>
                    <Input type="date" value={form.doc_dates?.[k] || ''} onChange={e => setForm(f => ({ ...f, doc_dates: { ...f.doc_dates, [k]: e.target.value } }))} data-testid={`vehicle-doc-${k}`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} data-testid="vehicle-active-switch" /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="vehicle-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Vehicles;
